/**
 * React explicitly deletes the autofocus attribute to work around SSR issues
 *
 * @see https://github.com/facebook/react/blob/main/packages/react-dom-bindings/src/client/ReactDOMComponent.js
 */
export const AUTOFOCUS_SELECTOR = '[data-autofocus]';

export const FOCUSABLE_SELECTOR =
  'button:enabled, a, [href], input:enabled, select:enabled, textarea:enabled';

/**
 * Elements that are part of the sequential Tab order: natively focusable
 * controls plus anything made focusable via `[tabindex]`, excluding
 * `[tabindex="-1"]` (focusable programmatically but skipped by Tab).
 *
 * Use this — not `FOCUSABLE_SELECTOR` — when reconstructing keyboard tab order,
 * so `<div tabindex="0">` / `<svg tabindex="0">` are not skipped.
 */
export const TABBABLE_SELECTOR =
  'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

/**
 * Attempts to find the first focusable element within a container.
 * Returns the first element matching the autofocus selector, then the first focusable
 * element not matching the exclude selector, then any focusable element, and finally
 * the container itself if no focusable elements are found.
 *
 * To override the default focus behavior, you can add the `data-autofocus` attribute
 * to the element you want to focus.
 *
 * @param container - The container element to search within
 * @param excludeSelector - Optional CSS selector for elements to exclude from focus search
 */
export const getFocusableElement = (
  container: HTMLElement,
  excludeSelector?: string
): HTMLElement => {
  const autofocusElement =
    container.querySelector<HTMLElement>(AUTOFOCUS_SELECTOR);

  if (autofocusElement) {
    return autofocusElement;
  }

  if (excludeSelector) {
    const focusableElementOutsideExcluded =
      container.querySelector<HTMLElement>(
        `:is(${FOCUSABLE_SELECTOR}):not(${excludeSelector})`
      );

    if (focusableElementOutsideExcluded) {
      return focusableElementOutsideExcluded;
    }
  }

  const focusableElement =
    container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

  if (focusableElement) {
    return focusableElement;
  }

  return container;
};

/**
 * Returns all tabbable elements within a container, in DOM order (the sequential
 * Tab order). Includes `[tabindex]`-focusable elements and excludes
 * `[tabindex="-1"]`. Unlike `getFocusableElement`, it does not prioritise
 * `[data-autofocus]`. Callers that care about visibility filter the result
 * themselves.
 *
 * @param container - The container element to search within
 * @param excludeSelector - Optional CSS selector for elements to exclude
 */
export const getTabbableElements = (
  container: ParentNode,
  excludeSelector?: string
): HTMLElement[] =>
  Array.from(
    container.querySelectorAll<HTMLElement>(
      excludeSelector
        ? `:is(${TABBABLE_SELECTOR}):not(${excludeSelector})`
        : TABBABLE_SELECTOR
    )
  );
